import { NavigationType } from 'react-router-dom';
import { RouteConfig, RouteTransition } from '../../../utils/routeConfig';
import { ROUTES } from '../../../constants/routes';

export type TransitionDirection = 'forward' | 'backward';

export interface ResolvedTransition {
  type: RouteTransition;
  direction: TransitionDirection;
}

/**
 * Bottom-nav roots — when navigating between them, use peer crossfade
 * rather than hierarchical slide.
 */
const PEER_ROOTS: readonly string[] = [
  ROUTES.OVERVIEW,
  ROUTES.ASSETS,
  ROUTES.BUDGET,
  ROUTES.HISTORY,
] as const;

const rootSegment = (pathname: string): string => {
  const slash = pathname.indexOf('/', 1);
  return slash === -1 ? pathname : pathname.slice(0, slash);
};

const arePeers = (prevPath: string, nextPath: string): boolean => {
  const a = rootSegment(prevPath);
  const b = rootSegment(nextPath);
  return a !== b && PEER_ROOTS.includes(a) && PEER_ROOTS.includes(b);
};

/**
 * Decide how two pathnames should animate.
 *
 *   1. If either side is explicitly `modal`, use modal transition.
 *      Direction still follows push/pop.
 *   2. Otherwise the incoming route's `transition` override wins.
 *   3. Otherwise: peer tabs → fade, everything else → slide.
 */
export function resolveTransition(
  prevPath: string,
  nextPath: string,
  prevMatch: RouteConfig | null,
  nextMatch: RouteConfig | null,
  navigationType: NavigationType,
): ResolvedTransition {
  const direction: TransitionDirection = navigationType === 'POP' ? 'backward' : 'forward';

  if (prevMatch?.transition === 'modal' || nextMatch?.transition === 'modal') {
    return { type: 'modal', direction };
  }

  if (nextMatch?.transition) {
    return { type: nextMatch.transition, direction };
  }

  if (arePeers(prevPath, nextPath)) {
    return { type: 'fade', direction };
  }

  return { type: 'slide', direction };
}
